import { matchPath, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { RouterAddress } from "../../constants/RouterAddress";
import AppHeader from "../layout/AppHeader";
import AppBreadcrumbs from "../layout/AppBreadcrumbs";
import { useEffect, useState } from "react";
import { redirectToHomeForAuthMode } from "../../utils/stringUtils";
import Unauthorized from "../pages/Unauthorized";

export function PublicRoutes() {
  const location = useLocation();
  const hideHeader = Boolean(
    matchPath({ path: RouterAddress.PUBLIC_WEBSITE, end: true }, location.pathname)
    || matchPath({ path: RouterAddress.PUBLIC_WEBSITE_SUBPAGE, end: true }, location.pathname)
    || matchPath({ path: RouterAddress.PUBLISH_WEB, end: true }, location.pathname)
    || matchPath({ path: RouterAddress.PUBLISH_WEB_SUBPAGE, end: true }, location.pathname)
  );

  return (
    <>
      {!hideHeader ? <AppHeader /> : null}
      <Outlet />
    </>
  );
}

export function PrivateRoutes({ authStore }: any) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const authenticated = await authStore.isUserLoggedIn();
        setIsAuthenticated(authenticated);
        if (!authenticated) {
          navigate(RouterAddress.LOGIN_PAGE, { replace: true });
        }
      } catch (err) {
        console.error(err);
        navigate(RouterAddress.LOGIN_PAGE, { replace: true });
      } finally {
        setIsCheckingAuth(false);
      }
    })();
  }, [authStore, navigate]);

  if (isCheckingAuth) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to={RouterAddress.LOGIN_PAGE} replace />;
  }

  return (
    <>
      <AppHeader />
      <AppBreadcrumbs />
      <Outlet />
    </>
  );
}

export function PublicOnlyRoutes({ authStore }: any) {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const authenticated = await authStore.isUserLoggedIn();
        if (authenticated) {
          const roleType = authStore.getUserRoleType();
          setRedirectPath(
            roleType
              ? redirectToHomeForAuthMode(authStore.getAuthMode?.() || "legacy_cognito_role", roleType)
              : RouterAddress.APP_ROOT,
          );
        }
      } catch {
        setRedirectPath(null);
      } finally {
        setIsCheckingAuth(false);
      }
    })();
  }, [authStore]);

  if (isCheckingAuth) {
    return null;
  }

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  return <Outlet />;
}

export function AdminRoutes({ authStore }: any) {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAdminUser, setIsAdminUser] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const authenticated = await authStore.isUserLoggedIn();
        setIsAdminUser(authenticated && !!authStore.isAdmin?.());
      } catch {
        setIsAdminUser(false);
      } finally {
        setIsCheckingAuth(false);
      }
    })();
  }, [authStore]);

  if (isCheckingAuth) {
    return null;
  }

  if (!isAdminUser) {
    return <Unauthorized />;
  }

  return <Outlet />;
}
